/**
 * ANN-12 display Slice 2 — per-viewer consultant/tier price line.
 *
 * The DBM v0.6 projection sets variant.price = campaign base and compareAtPrice = RSP, so
 * snippets/price-list.liquid already renders the public 2-line ladder (~~RSP~~ campaign-base)
 * natively for everyone. This module adds ONLY the third line: for a logged-in DISCOUNTED-tier
 * viewer (consultant / staff / boutique / internal) it splices their NET price in as the live
 * line, striking the campaign base above it.
 *
 * Source of truth for "am I signed in / what is my price" is the DBM App Proxy — NOT the Liquid
 * `customer` object (which is nil in the cached header under New Customer Accounts). This mirrors
 * the consultant-status probe in sections/login-bar.liquid: one fetch, JSON, graceful degrade.
 *
 *   GET /apps/consultant/price?skus=SKU1,SKU2 ->
 *     { signed_in, viewer_tier, viewer_rate,
 *       prices: [{ sku, rsp_inc_vat, public_price_inc_vat, viewer_price_inc_vat, campaign_active }] }
 *
 * Progressive enhancement: the native ladder renders first (no flash-to-blank); this only ever
 * ADDS a line in place once the response lands. Logged-out / signed_in:false / endpoint error /
 * viewer_price >= public_price => the native ladder is left exactly as the theme rendered it.
 */
(function () {
  'use strict';

  // App Proxy: Shopify maps storefront /apps/consultant/<REST> -> {backend root}/<REST>, and the
  // DBM controllers live at /api/extension/* — so the storefront path carries the /api/extension
  // prefix (exactly like the consultant-status probe in sections/login-bar.liquid). Reaches the
  // AppProxyPriceController [HttpGet("price")] at /api/extension/price.
  var ENDPOINT = '/apps/consultant/api/extension/price';
  var CACHE_KEY = 'dbm_viewer_prices'; // sessionStorage: { signed_in, prices: { SKU: entry } }
  var APPLIED_ATTR = 'data-dbm-ladder-applied'; // marks a node we've already rewritten (per SKU)

  // -------------------------------------------------------------------------------------------
  // Money formatting — match the theme's existing rendered currency exactly.
  //
  // The theme renders money with {{ ... | money }} (currency code optional). For product/card
  // contexts it also rounds UP to the nearest whole rand and strips a trailing ".00"
  // (snippets/price-list.liquid). We derive the literal prefix/suffix (e.g. "R ", " ZAR") from a
  // price node the theme already rendered on THIS page, so the third line is visually identical
  // regardless of currency settings or locale.
  // -------------------------------------------------------------------------------------------

  // Pull the currency "shape" out of an already-rendered money string, e.g. "R 1 234,50".
  function parseMoneyShape(text) {
    if (!text) return null;
    var m = String(text).match(/^([^0-9]*)([0-9][0-9.,\s]*[0-9]|[0-9])([^0-9]*)$/);
    if (!m) return null;
    var numeric = m[2];
    // Detect decimal separator: the last '.' or ',' followed by exactly two digits is the cents sep.
    var decimalSep = null;
    var dm = numeric.match(/[.,](\d{2})$/);
    if (dm) {
      decimalSep = numeric.charAt(numeric.length - 3);
    }
    // The thousands separator is whatever non-digit char appears before the decimal portion
    // (space, comma, dot, or nothing). We only need it to mirror grouping.
    var integerPart = decimalSep ? numeric.slice(0, numeric.length - 3) : numeric;
    var thousandsSep = '';
    var tm = integerPart.match(/\d([^\d])\d{3}/);
    if (tm) thousandsSep = tm[1];
    return {
      prefix: m[1],
      suffix: m[3],
      decimalSep: decimalSep,
      thousandsSep: thousandsSep
    };
  }

  // Format a rand amount (decimal string from the endpoint) using a parsed shape + theme rules.
  // `roundWhole` mirrors the product/card rounding (ceil to whole rand, drop ".00").
  function formatMoney(randStr, shape, roundWhole) {
    var amount = parseFloat(randStr);
    if (!isFinite(amount)) return null;
    shape = shape || { prefix: 'R ', suffix: '', decimalSep: '.', thousandsSep: ' ' };

    var cents;
    if (roundWhole) {
      amount = Math.ceil(amount); // ceil to whole rand (theme: divided_by:100|ceil|times:100)
      cents = false;
    } else {
      cents = true;
    }

    var intPart = Math.floor(Math.abs(amount));
    var fracPart = Math.round((Math.abs(amount) - intPart) * 100);
    if (fracPart === 100) {
      intPart += 1;
      fracPart = 0;
    }

    // Group the integer part with the theme's thousands separator.
    var intStr = String(intPart);
    if (shape.thousandsSep) {
      intStr = intStr.replace(/\B(?=(\d{3})+(?!\d))/g, shape.thousandsSep);
    }

    var out = intStr;
    var sep = shape.decimalSep || '.';
    if (cents) {
      // line_item context keeps cents; but still drop ".00" to mirror the product/card display
      // when whole — keeps the three lines visually consistent. If non-zero, show two digits.
      if (fracPart !== 0) {
        out += sep + (fracPart < 10 ? '0' + fracPart : String(fracPart));
      }
    }
    return shape.prefix + out + shape.suffix;
  }

  // -------------------------------------------------------------------------------------------
  // DOM rewrite — splice the viewer NET line into one <price-list>.
  // -------------------------------------------------------------------------------------------

  // Per-context typography classes mirroring snippets/price-list.liquid, so the struck lines match
  // the surrounding context even when the native ladder had NO compare-at-price node to copy from
  // (the no-campaign case: public == RSP, so the theme rendered a single regular-priced line).
  var CONTEXT_CLASSES = {
    product: { sale: 'h6 text-on-sale', compare: 'h7 line-through' },
    card: { sale: 'subheading text-on-sale', compare: 'subheading text-subdued line-through' },
    line_item: { sale: 'subheading text-on-sale', compare: 'subheading text-subdued line-through' }
  };

  function classesFor(priceList) {
    var ctx = priceList.getAttribute('data-dbm-context') || 'product';
    var fallback = CONTEXT_CLASSES[ctx] || CONTEXT_CLASSES.product;
    // Prefer the live sale-price class the theme actually rendered (it already encodes the right
    // size for this context, incl. the heading/body_text card variants); fall back per context.
    var existingSale = priceList.querySelector('sale-price');
    var existingCompare = priceList.querySelector('compare-at-price');
    return {
      sale: (existingSale && existingSale.getAttribute('class')) || fallback.sale,
      // For the struck lines always use the context's compare class — the native sale-price class
      // (which may be a non-struck "regular" class in the no-campaign case) must NOT leak here.
      compare: (existingCompare && existingCompare.getAttribute('class')) || fallback.compare
    };
  }

  // The ONLY visible label in the consultant price block (per the DB mockup): the "RSP:" prefix on
  // the DTC box. Overridable via window.dbmPriceLabels.rsp; English-default store needs no locale key.
  var LABELS = {
    rsp: (window.dbmPriceLabels && window.dbmPriceLabels.rsp) || 'RSP:'
  };

  var STYLE_ID = 'dbm-price-ladder-style';

  // Inject the layout CSS ONCE (idempotent by id). Typography (size / font / colour) is inherited
  // from the native <sale-price>/<compare-at-price> tags — here we only lay the blocks out.
  function ensureLadderStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '.dbm-cprice{display:flex;align-items:center;gap:1.1rem;flex-wrap:wrap}' +
      '.dbm-cprice__main,.dbm-cprice__dtc{display:flex;align-items:baseline;gap:.5rem}' +
      '.dbm-cprice__dtc{padding:.4rem .8rem;background:rgba(0,0,0,.05);border-radius:.25rem}' +
      '.dbm-cprice__rsp{color:rgb(103,113,103)}' +
      // Card only (scoped on data-dbm-context so the PDP block is unchanged): tighten the gap and
      // shrink the RSP box — smaller padding + smaller text — so the net price and the RSP box sit on
      // ONE line instead of the box wrapping below.
      'price-list[data-dbm-context="card"] .dbm-cprice{gap:.5rem}' +
      'price-list[data-dbm-context="card"] .dbm-cprice__dtc{padding:.12rem .45rem;gap:.3rem}' +
      'price-list[data-dbm-context="card"] .dbm-cprice__dtc .dbm-cprice__rsp,' +
      'price-list[data-dbm-context="card"] .dbm-cprice__dtc compare-at-price{font-size:.78rem!important;line-height:1.35}' +
      '.dbm-price-ladder--compact{display:flex;flex-direction:row;align-items:baseline;gap:.5rem;flex-wrap:wrap}';
    (document.head || document.documentElement).appendChild(style);
  }

  // Create a native price element (<sale-price>/<compare-at-price>) so it inherits the theme's
  // price typography. Plain spans pick up the heading serif at the wrong size, so we MUST use the
  // tags. A bare element renders ONLY the price text (no injected "Sale price"); the `line-through`
  // class is what strikes a reference price.
  function priceEl(tagName, cls, text, isNet) {
    var el = document.createElement(tagName);
    if (cls) el.className = cls;
    if (isNet) el.setAttribute('data-dbm-viewer', '');
    el.textContent = text;
    return el;
  }

  // The compare-at class WITHOUT the strike — for the non-struck "RSP: R649" value in the DTC box.
  function plainCompareCls(cls) { return cls.compare.replace(/\bline-through\b/g, '').trim(); }

  // Consultant price block — used on BOTH the PDP and the product card, so a special offer reads the
  // same in the grid as on the product page.
  //
  // Annique (11 Sep 2026): the consultant sees the SHOPPER price as her main price, the same rule as her
  // cart (snippets/cart-shopper-total.liquid); her tier price is not shown until checkout.
  //   promo    -> Block 1: <promo price>  (the figure that stood struck until today, now the main price,
  //                        nothing struck)   Block 2: [ RSP: <RSP> ]
  //   standard -> Block 1: <RSP>            (no struck price, so the RSP is the main price)   no Block 2
  // History: 30 Jun to 10 Sep the block was <net> + <struck public> and, from 10 Sep, a plain RSP badge on
  // every product. Sheldon, 11 Sep: "when the price is crossed out we use that as the main price (dont show
  // the crossed out value) then if its not crossed out we use the RSP as the main price and dont show the
  // RSP block".
  function buildConsultantBlocks(cls, isPromo, viewerStr, publicStr, rspStr) {
    var wrap = document.createElement('div');
    wrap.className = 'dbm-cprice';
    wrap.setAttribute('data-dbm-ladder', '');

    // Block 1 — the shopper price: the promo price on a markdown, otherwise the RSP.
    var showBadge = isPromo && rspStr != null;
    var mainStr = showBadge ? publicStr : (rspStr != null ? rspStr : publicStr);
    var b1 = document.createElement('div');
    b1.className = 'dbm-cprice__main';
    b1.appendChild(priceEl('sale-price', cls.sale, mainStr, true));
    wrap.appendChild(b1);

    // Block 2 — the RSP badge, only on a markdown (the main price is then the promo price).
    if (showBadge) {
      var b2 = document.createElement('div');
      b2.className = 'dbm-cprice__dtc';
      var lbl = document.createElement('span');
      lbl.className = 'dbm-cprice__rsp';
      lbl.textContent = LABELS.rsp;
      b2.appendChild(lbl);
      // Sheldon (10 Sep 2026): the badge is the plain RSP figure, never struck.
      b2.appendChild(priceEl('compare-at-price', plainCompareCls(cls), rspStr));
      wrap.appendChild(b2);
    }
    return wrap;
  }

  function rewriteLadder(priceList, entry) {
    var ctx = priceList.getAttribute('data-dbm-context') || 'product';
    // The PDP AND the product card render the full two-block consultant layout (net + a grey RSP box),
    // so a special offer looks the same in the grid as on the product page; only cart line items keep
    // the tight single-line form.
    var useBlocks = ctx === 'product' || ctx === 'card';
    var roundWhole = ctx !== 'line_item';

    // Derive currency shape from a money string the theme already rendered in this list.
    // The native Horizon <sale-price>/<compare-at-price> elements inject a VISIBLE caption
    // ("Sale price" / "Regular price") into their text, so the raw textContent is e.g.
    // "Sale priceR 159". Strip those captions first — otherwise parseMoneyShape's greedy
    // non-digit prefix becomes "Sale priceR " and every rung re-renders the caption.
    var sampleNode = priceList.querySelector('sale-price, compare-at-price');
    var sampleText = sampleNode ? sampleNode.textContent : '';
    ['Sale price', 'Regular price'].forEach(function (w) { sampleText = sampleText.split(w).join(' '); });
    sampleText = sampleText.replace(/\s+/g, ' ').trim();
    var shape = parseMoneyShape(sampleText);

    // Net is the EXACT charged price (cents, e.g. R 127,20) — never ceil-rounded. The struck
    // reference lines mirror the theme's whole-rand product/card display.
    var viewerStr = formatMoney(entry.viewer_price_inc_vat, shape, false);
    var publicStr = formatMoney(entry.public_price_inc_vat, shape, roundWhole);
    var rspStr = formatMoney(entry.rsp_inc_vat, shape, roundWhole);
    if (viewerStr == null || publicStr == null) return; // can't format -> leave native ladder

    var cls = classesFor(priceList);
    // "On promo" = a GENUINE markdown: the public price sits BELOW the RSP. (Our campaign_active
    // signal is true even for STANDARD monthly price-lists, so gate on the actual markdown — a
    // STANDARD-priced product where public == RSP is NOT on promo and shows no DTC block.)
    var isPromo = entry.rsp_inc_vat &&
      parseFloat(entry.rsp_inc_vat) > parseFloat(entry.public_price_inc_vat);

    ensureLadderStyle();

    var node;
    if (useBlocks) {
      // PDP + product card — the consultant price block: shopper price, plus the RSP badge on a markdown.
      node = buildConsultantBlocks(cls, isPromo, viewerStr, publicStr, rspStr);
    } else {
      // Line items (order pages; the cart passes hide_unit_price and never mounts this) — the same rule
      // in its tight form: the shopper price alone, nothing struck (Annique, 11 Sep 2026).
      node = document.createElement('div');
      node.className = 'dbm-price-ladder--compact';
      node.setAttribute('data-dbm-ladder', '');
      node.appendChild(priceEl('sale-price', cls.sale, publicStr, true));
    }

    // Preserve any trailing unit-price node the theme rendered.
    var unitPrice = priceList.querySelector('unit-price');

    // Remove the native price nodes AND any prior block we built — DIRECT children only, so we
    // don't also match (and double-remove) the price tags nested inside our own block. Idempotent.
    var toRemove = priceList.querySelectorAll(
      ':scope > sale-price, :scope > compare-at-price, :scope > .dbm-cprice, :scope > .dbm-price-ladder--compact');
    for (var i = 0; i < toRemove.length; i++) {
      if (toRemove[i].parentNode) toRemove[i].parentNode.removeChild(toRemove[i]);
    }

    if (unitPrice) {
      priceList.insertBefore(node, unitPrice);
    } else {
      priceList.appendChild(node);
    }
    priceList.setAttribute(APPLIED_ATTR, entry.sku);
  }

  // -------------------------------------------------------------------------------------------
  // Apply a price map to every price-list currently in the DOM.
  // -------------------------------------------------------------------------------------------

  function apply(state, root) {
    if (!state || !state.signed_in || !state.prices) return;
    var scope = root || document;
    var lists = scope.querySelectorAll('price-list[data-dbm-sku]');
    for (var i = 0; i < lists.length; i++) {
      var pl = lists[i];
      var sku = pl.getAttribute('data-dbm-sku');
      if (!sku) continue;
      var entry = state.prices[sku];
      if (!entry) continue;

      var viewer = parseFloat(entry.viewer_price_inc_vat);
      var pub = parseFloat(entry.public_price_inc_vat);
      // Only rewrite when the viewer has a REAL discount vs the public price.
      if (!isFinite(viewer) || !isFinite(pub) || !(viewer < pub)) continue;
      // Idempotency: skip if we've already applied THIS sku to THIS node (survives re-runs).
      if (pl.getAttribute(APPLIED_ATTR) === sku) continue;

      try {
        rewriteLadder(pl, entry);
      } catch (e) {
        /* leave the native ladder untouched on any error */
      }
    }
  }

  // -------------------------------------------------------------------------------------------
  // Fetch — one batched request for all SKUs visible on the page.
  // -------------------------------------------------------------------------------------------

  function collectSkus(root) {
    var scope = root || document;
    var lists = scope.querySelectorAll('price-list[data-dbm-sku]');
    var seen = {};
    var skus = [];
    for (var i = 0; i < lists.length; i++) {
      var sku = lists[i].getAttribute('data-dbm-sku');
      if (sku && !seen[sku]) {
        seen[sku] = true;
        skus.push(sku);
      }
    }
    return skus;
  }

  function indexPrices(data) {
    var map = {};
    if (data && Array.isArray(data.prices)) {
      for (var i = 0; i < data.prices.length; i++) {
        var p = data.prices[i];
        if (p && p.sku) map[p.sku] = p;
      }
    }
    return { signed_in: !!(data && data.signed_in), viewer_tier: data && data.viewer_tier, prices: map };
  }

  var lastState = null; // freshest known state, used to re-apply after re-renders.

  function refresh(root) {
    var skus = collectSkus(root);
    if (!skus.length) return;

    // Stale-while-revalidate: apply cached state immediately so a re-rendered node gets the
    // viewer line back without waiting for the network.
    if (!lastState) {
      try {
        var cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) lastState = JSON.parse(cached);
      } catch (e) {}
    }
    if (lastState) apply(lastState, root);

    var url = ENDPOINT + '?skus=' + encodeURIComponent(skus.join(','));
    fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return; // endpoint error -> keep native ladder
        var state = indexPrices(data);
        lastState = state;
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(state)); } catch (e) {}
        apply(state); // apply across the whole document (new SKUs may have appeared)
      })
      .catch(function () { /* network error -> keep native ladder */ });
  }

  // -------------------------------------------------------------------------------------------
  // Wiring — initial run + survive the theme's re-renders.
  //
  // The PDP swaps price markup on variant switch (product:rerender / variant:change) and the cart
  // re-renders its line items (cart:change / cart-drawer:refreshed). After any of those, the freshly
  // rendered <price-list> nodes are native again, so we re-apply the (cached) viewer line in place
  // and revalidate. Capture-phase + microtask so we run after the theme finishes its swap.
  // -------------------------------------------------------------------------------------------

  function reapplySoon(root) {
    // Re-apply cached state synchronously-ish, then revalidate against the network.
    Promise.resolve().then(function () {
      if (lastState) apply(lastState, root);
      refresh(root);
    });
  }

  function init() {
    refresh();

    // PDP variant change: event bubbles to document (bubbles:true in theme.js).
    document.addEventListener('variant:change', function () { reapplySoon(); }, true);
    // Cart updates re-render line items.
    document.addEventListener('cart:change', function () { reapplySoon(); });
    document.addEventListener('cart-drawer:refreshed', function () { reapplySoon(); });
    // Some sections dispatch product:rerender on their form; catch it at the document level too.
    document.addEventListener('product:rerender', function () { reapplySoon(); }, true);

    // Cards injected AFTER load fire no event at all: <product-recommendations> ("Customers also bought")
    // fetches its section and replaceChildren()s the cards; the search drawer and quick buy do the same.
    // Those price-lists never saw a pass, so a consultant read the DTC price with no RSP badge there
    // (Sheldon, 10 Sep 2026). Watch the document for any newly added price-list we own and re-apply.
    // Our own writes add .dbm-cprice / price tags INSIDE an existing price-list, never a new price-list,
    // so they do not re-trigger this (and APPLIED_ATTR makes a second pass a no-op anyway).
    if (typeof MutationObserver === 'function') {
      var pending = false;
      var mo = new MutationObserver(function (records) {
        if (pending) return;
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if ((n.matches && n.matches('price-list[data-dbm-sku]')) ||
                (n.querySelector && n.querySelector('price-list[data-dbm-sku]'))) {
              pending = true;
              Promise.resolve().then(function () { pending = false; reapplySoon(); });
              return;
            }
          }
        }
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
